// Replace Color — replace specific colors in an image.
// Functional approximation: select a target color range and replace with
// a new color, with fuzziness control.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface ReplaceColorParams {
  targetR: number; // 0..255
  targetG: number; // 0..255
  targetB: number; // 0..255
  replacementR: number; // 0..255
  replacementG: number; // 0..255
  replacementB: number; // 0..255
  fuzziness: number; // 0..200
  lightness: number; // -100..100
  saturation: number; // -100..100
  hue: number; // -180..180
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h /= 360; s /= 100; l /= 100;
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function colorDistance(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): number {
  const dr = r1 - r2, dg = g1 - g2, db = b1 - b2;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

export const ReplaceColor: Filter<ReplaceColorParams> = {
  id: "replace-color",
  displayName: "Replace Color",
  defaultParams: {
    targetR: 255,
    targetG: 0,
    targetB: 0,
    replacementR: 0,
    replacementG: 0,
    replacementB: 255,
    fuzziness: 40,
    lightness: 0,
    saturation: 0,
    hue: 0,
  },

  apply(
    input: PixelBuffer,
    params: ReplaceColorParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    if (w === 0 || h === 0) {
      dst.set(src);
      return out;
    }

    const { targetR, targetG, targetB, replacementR, replacementG, replacementB, fuzziness, lightness, saturation, hue } = params;
    const [targetH, targetS, targetL] = rgbToHsl(targetR, targetG, targetB);
    const [repH, repS, repL] = rgbToHsl(replacementR, replacementG, replacementB);

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o]!, g = src[o + 1]!, b = src[o + 2]!;
      const [h, s, l] = rgbToHsl(r, g, b);

      // Compute how close this pixel is to the target color
      const dist = colorDistance(r, g, b, targetR, targetG, targetB);
      const maxDist = fuzziness * 1.732; // sqrt(3) * 255 max
      const match = Math.max(0, 1 - dist / maxDist);

      if (match > 0) {
        // Compute new HSL values
        let newH = h + hue;
        if (newH < 0) newH += 360;
        if (newH >= 360) newH -= 360;
        let newS = Math.max(0, Math.min(100, s + saturation));
        let newL = Math.max(0, Math.min(100, l + lightness));

        // Blend between original and replacement based on match strength
        const [newR, newG, newB] = hslToRgb(newH, newS, newL);
        dst[o] = clamp8(Math.round(r + (newR - r) * match));
        dst[o + 1] = clamp8(Math.round(g + (newG - g) * match));
        dst[o + 2] = clamp8(Math.round(b + (newB - b) * match));
      } else {
        dst[o] = r;
        dst[o + 1] = g;
        dst[o + 2] = b;
      }
      dst[o + 3] = src[o + 3]!;
    }

    return out;
  },
};
